import type { ReactNode } from "react";

import type { MetricKey, Scope } from "@busright/data-contract";

import { useDrill } from "@/context/DrillContext";
import { getMetricDef } from "@/lib/metric-registry";

// =================================================================================================
// Drillable — make ANY KPI / stat / chart-slice open the DatasetDrawer for its {metricKey, scope}.
//
// The complement to `ClickableCard` (which navigates to a page). `Drillable` instead opens the
// universal dataset drawer in place — "arrows drill into the insight they show", never to a random
// page. It renders a real keyboard control (button) so Enter/Space activate it and it gets a focus
// ring; a corner ↘ affordance signals "this opens the dataset behind the number".
//
//   <Drillable metricKey="fleet.criticalSharePct" scope={fleetScope}>
//     <KpiCard label="% critical" value={formatPct(pct)} source="…" />
//   </Drillable>
//
// Wrap only NON-interactive content (a KPI, a stat, a static chart panel). For TABLE ROWS the drawer
// has its own row-drill; for page navigation use ClickableCard.
// =================================================================================================

export interface DrillableProps {
  readonly metricKey: MetricKey;
  // The scope whose dataset to open (defaults to the metric's natural scope at the call site).
  readonly scope: Scope;
  readonly children: ReactNode;
  // Optional accessible-name override (defaults to "Drill into <metric label>").
  readonly label?: string;
}

export const Drillable = ({ metricKey, scope, children, label }: DrillableProps): ReactNode => {
  const { openDrill } = useDrill();
  const def = getMetricDef(metricKey);
  const accessibleName = label ?? `Drill into ${def?.label ?? metricKey}`;
  return (
    <button
      type="button"
      className="drillable"
      aria-label={accessibleName}
      onClick={() => openDrill(metricKey, scope)}
    >
      <span className="drillable-affordance" aria-hidden>
        ↘
      </span>
      {children}
    </button>
  );
};
